import { OnSendDeleteConfirmation } from '~/features/events';
import { sendDeleteConfirmation } from '~/features/utils';
import { CONFIRMATION_VIEW_STATE, MED_LICENSE_STATE } from '~/features/xstateSystem';
import { emptyForm } from './constants';
import {
  DeleteMedicalLicenseEvent,
  EditMedicalLicenseEvent,
  FilterMedicalLicenseEvent,
  MedicalLicenseResponseEvent,
  MedicalLicensesResponseEvent,
  MedicalLicenseTypesResponseEvent,
  SortMedicalLicenseEvent,
} from './events';
import { MedicalLicenseContext, Scope } from './types';
import { medTypeOptionsProvider } from './utils';

export const toggleLoading = (machineScope: Scope): Partial<MedicalLicenseContext> => {
  return {
    loading: !machineScope.context.loading,
  };
};

export const openCreateModal = (): Partial<MedicalLicenseContext> => {
  return {
    modal: {
      create: true,
      edit: false,
    },
  };
};

export const openEditModal = (scope: Scope): Partial<MedicalLicenseContext> => {
  const event = scope.event as EditMedicalLicenseEvent;
  const medicalLicenseFormData =
    scope.context.medicalLicenses.find((license) => license.id === event.payload.id) || emptyForm;
  return {
    medLicensesForm: medicalLicenseFormData,
    modal: {
      create: false,
      edit: true,
    },
  };
};

export const closeModal = (): Partial<MedicalLicenseContext> => {
  return {
    medLicensesForm: emptyForm,
    modal: {
      create: false,
      edit: false,
    },
  };
};

export const setMedicalSort = (machineScope: Scope): Partial<MedicalLicenseContext> => {
  const event = machineScope.event as SortMedicalLicenseEvent;
  return {
    sort: {
      ...machineScope.context.sort,
      sortBy: event.sortBy,
      [event.sortBy]: -1 * machineScope.context.sort[event.sortBy],
    },
  };
};

export const setMedicalLicenses = (machineScope: Scope): Partial<MedicalLicenseContext> => {
  const { output } = machineScope.event as MedicalLicensesResponseEvent;
  return {
    loading: false,
    modal: { create: false, edit: false },
    medicalLicenses: output,
  };
};

export const setMedicalLicenseTypeOptions = (
  machineScope: Scope
): Partial<MedicalLicenseContext> => {
  const event = machineScope.event as MedicalLicenseTypesResponseEvent;
  return {
    loading: false,
    _medicalLicenseTypeOptions: medTypeOptionsProvider(event.output),
  };
};

export const resetMachine = (): Partial<MedicalLicenseContext> => {
  return {
    medLicensesForm: emptyForm,
    loading: false,
    modal: { edit: false, create: false },
  };
};

export const filterMedicalLicenseTable = (machineScope: Scope): Partial<MedicalLicenseContext> => {
  const event = machineScope.event as FilterMedicalLicenseEvent;
  return {
    filter: {
      ...machineScope.context.filter,
      [event.payload.type]: event.payload.value,
    },
  };
};

export const updateLicenses = (machineScope: Scope): Partial<MedicalLicenseContext> => {
  const event = machineScope.event as MedicalLicenseResponseEvent;
  let medicalLicenses = machineScope.context.medicalLicenses;
  if (medicalLicenses.find((license) => license.id === event.output.id)) {
    medicalLicenses = medicalLicenses.filter((license) => license.id != event.output.id);
  }
  medicalLicenses.push(event.output);

  return {
    medLicensesForm: emptyForm,
    medicalLicenses: [...medicalLicenses],
    modal: {
      create: false,
      edit: false,
    },
  };
};

export const setIdToDelete = (machineScope: Scope): Partial<MedicalLicenseContext> => {
  const event = machineScope.event as DeleteMedicalLicenseEvent;
  return {
    idToDelete: event.payload.id,
  };
};

export const removeLicense = (machineScope: Scope): Partial<MedicalLicenseContext> => {
  return {
    medicalLicenses: machineScope.context.medicalLicenses.filter(
      (license) => license.id != machineScope.context.idToDelete
    ),
  };
};

export const openDeleteConfirmation = (scope: Scope) => {
  const event = scope.event as OnSendDeleteConfirmation;

  const onConfirm = (id: number) => {
    scope.system.get(MED_LICENSE_STATE).send({ type: 'ON_DELETE', payload: { id } });
  };
  scope.system
    .get(CONFIRMATION_VIEW_STATE)
    .send(sendDeleteConfirmation(() => onConfirm(event.payload.id)));
};
